import get from 'lodash/get'
import { useState } from 'react'
import {
  LangValue,
  LocalizedNews,
  MaybeLangValue,
  MaybeLocalizedOptionsList,
  WrapperProps
} from '../components'
import { DEFAULT_UI_LANGUAGE_VALUE } from '../constants'
import { NewsQueryType, TranslationQueryType } from '../types/types'
import { getNewsTranslationsForLang } from '../utils'
import { createContextFactory } from './utils'

type MaybeTranslationQueryType = TranslationQueryType | undefined

type MaybeNewsQueryType = NewsQueryType | undefined

const getLang = (lang: MaybeLangValue): LangValue => {
  return lang || DEFAULT_UI_LANGUAGE_VALUE
}

const getLocalizedOptionsLists = (
  optionsLists: MaybeTranslationQueryType,
  languageKey: LangValue = DEFAULT_UI_LANGUAGE_VALUE
): MaybeLocalizedOptionsList =>
  optionsLists
    ? {
        // localizedBusinessLanguageOptions: get(
        //   optionsLists.options.businessLanguages,
        //   languageKey
        // ),
        localizedCountryOptions: get(optionsLists.countries, languageKey),
        localizedEmailTypeOptions: get(optionsLists.emailTypes, languageKey),
        localizedExemptionReasonOptions: get(
          optionsLists.exemptionReasons,
          languageKey
        ),
        localizedLanguageOptions: get(optionsLists.languages, languageKey),
        localizedPostalCodeOptions: get(optionsLists.postalCodes, languageKey)
      }
    : undefined

const getLocalizedNews = (
  newsData: MaybeNewsQueryType,
  languageKey: LangValue = DEFAULT_UI_LANGUAGE_VALUE
): LocalizedNews => ({
  localizedNewsList: getNewsTranslationsForLang(
    languageKey,
    newsData?.news || []
  )
})

export interface TranslationData {
  UILanguage: LangValue
  UILanguageUpdated: boolean
  selectUILanguage: (language: LangValue) => void
  setOptionsLists: (updatedOptions: TranslationQueryType) => void
  localizedOptionsLists: MaybeLocalizedOptionsList
  setNewsData: (newsData: NewsQueryType) => void
  localizedNews: LocalizedNews
}

const [useTranslationData, TranslationContextProvider] =
  createContextFactory<TranslationData>(
    'useTranslationData',
    'TranslationContextProvider'
  )

const TranslationProvider = ({ children }: WrapperProps) => {
  const [UILanguage, setUILanguage] = useState<LangValue>(
    getLang(DEFAULT_UI_LANGUAGE_VALUE)
  )
  const [UILanguageUpdated, setUILanguageUpdated] = useState<boolean>(false)

  const selectUILanguage = (updatedUILanguage: LangValue) => {
    setUILanguage(updatedUILanguage)
    setUILanguageUpdated(true)
  }

  const [optionsLists, setOptionsLists] =
    useState<MaybeTranslationQueryType>(undefined)

  const [newsData, setNewsData] = useState<MaybeNewsQueryType>(undefined)

  return (
    <TranslationContextProvider
      value={{
        UILanguage,
        UILanguageUpdated,
        selectUILanguage,
        setOptionsLists,
        localizedOptionsLists: getLocalizedOptionsLists(
          optionsLists,
          UILanguage
        ),
        setNewsData,
        localizedNews: getLocalizedNews(newsData)
      }}
    >
      {children}
    </TranslationContextProvider>
  )
}

export { TranslationProvider, useTranslationData }
